import { useNotify, useRefresh } from 'react-admin';
import { useDispatch } from 'react-redux';
import { openDialog, setDialogMessage, setDialogTitle, setOnCloseKey } from '@/store/slices/dialog';
import { setIsLoading } from '@/store/slices/turnAdmin';
import dataProvider from '@/services/api';
import { NotifyProps } from '@/types/global';
import { DIALOG_DEFAULT_MESSAGE, NOTIFY_DEFAULT_MESSAGE } from './constants';
import { DialogParams, HandleResponseParams, RunRequestParams, UseRunRequestProps } from './types';
import { reloadPage } from './utils';

/**
 * useRenderRequest
 *
 * Centralizes request execution logic for admin actions.
 * Handles:
 * - loading state
 * - API execution
 * - success/error notifications
 * - dialog rendering
 * - page refresh / reload behavior
 *
 * Consumers configure behavior via RunRequestParams instead of re-implementing flows.
 */
const useRenderRequest = (props?: UseRunRequestProps) => {
  const { onCloseDialogKey } = props || {};
  const notify = useNotify();
  const refresh = useRefresh();
  const dispatch = useDispatch();

  /**
   * Initializes and opens a dialog with the provided title and message.
   * Optionally sets a custom onCloseDialogKey if provided by the hook consumer.
   */
  const initDialog = ({ message, title }: DialogParams) => {
    dispatch(setDialogTitle(title));
    dispatch(setDialogMessage(message));
    !!onCloseDialogKey && dispatch(setOnCloseKey(onCloseDialogKey));
    dispatch(openDialog());
  };

  /**
   * Handles successful responses using react-admin notifications.
   * Falls back to default success messaging and refreshes the page
   * unless a postAction override is provided.
   */
  const handleNotifiedResponse = ({ message: _message, options, postAction }: NotifyProps) => {
    const message = _message ?? NOTIFY_DEFAULT_MESSAGE.success;

    notify(message, {
      ...options,
      type: options?.type ?? 'info',
    });

    postAction ? postAction() : refresh();
  };

  /**
   * Handles successful responses using dialogs and/or page reload logic.
   * Determines behavior based on response shape and provided dialog params.
   * Always clears loading state.
   */
  const handleResponse = ({ message, response, title }: HandleResponseParams) => {
    if (!response?.data) {
      console.error(response);
      return;
    }

    if (response?.data?.state) {
      reloadPage();
      return;
    }

    const hasDialogContent = Boolean(message && title);

    if (!hasDialogContent) {
      reloadPage();
      return;
    }

    initDialog({ message, title });
    !onCloseDialogKey && setTimeout(reloadPage, 500);
  };

  /**
   * Handles errors by rendering a fallback dialog.
   * Attempts to extract structured error details from error.body
   * and falls back to a generic support message.
   */
  const handleError = (error: any) => {
    let message: string | React.ReactNode = null;

    if (error?.body) {
      const { message: errorMessage, request_id } = error?.body || {};
      message = (
        <span>
          {errorMessage && <p>{`Message: ${errorMessage}`}</p>}
          {request_id && <p>{`Request id: ${request_id}`}</p>}
        </span>
      );
    } else {
      message = DIALOG_DEFAULT_MESSAGE.error.message;
    }

    initDialog({ message, title: DIALOG_DEFAULT_MESSAGE.error.title });
  };

  /**
   * Handles errors using react-admin notifications.
   * Falls back to default error messaging and logs the original error.
   */
  const handleNotifiedError = (error: unknown, notifyProps?: NotifyProps) => {
    console.error(error);

    const message = notifyProps?.message || (error as any)?.body?.message || NOTIFY_DEFAULT_MESSAGE.error;
    const options = notifyProps?.options;

    notify(message, {
      ...options,
      type: options?.type ?? 'warning',
    });
  };

  /**
   * Executes a dataProvider request and routes the response
   * through dialogs, notifications, or default reload behavior
   * depending on the provided configuration.
   */
  const runRequest = async ({
    dialog,
    fetchType,
    mode = 'default',
    notify,
    params,
    resource,
    useResponseDialog,
  }: RunRequestParams) => {
    dispatch(setIsLoading(true));

    try {
      const response = await dataProvider(fetchType, resource, params);

      if (mode === 'silent') {
        return response;
      }

      // Notification-driven success handling
      if (mode === 'notify') {
        const message = notify?.onSuccess?.message || response?.data?.message;
        handleNotifiedResponse({ ...notify?.onSuccess, message });
        return response;
      }

      // Explicit dialog configuration
      if (mode === 'dialog' && dialog) {
        handleResponse({ message: dialog.message, response, title: dialog.title });
        return response;
      }

      // Dialog content derived from response payload
      if (useResponseDialog) {
        handleResponse({
          message: response?.data?.message,
          response,
          title: response?.data?.title,
        });
        return response;
      }

      // Default success behavior
      handleResponse({ response });
      return response;
    } catch (error) {
      if (mode === 'silent') return;

      notify ? handleNotifiedError(error, notify?.onError) : handleError(error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  return {
    runRequest,
  };
};

export default useRenderRequest;
